import {
  DeesElement,
  type TemplateResult,
  customElement,
  property,
  state,
  html,
} from '@design.estate/dees-element';

import * as domtools from '@design.estate/dees-domtools';
import * as interfaces from '../../interfaces/index.js';
import * as plugins from '../../00plugins.js';
import { demoFunc } from './demo.js';
import { appuiAppbarStyles } from './styles.js';
import { renderAppuiAppbar } from './template.js';
import { normalizeIconName } from '../appui.helpers.js';

// Import required components
import '../../00group-utility/dees-icon/dees-icon.js';
import '../../00group-utility/dees-windowcontrols/dees-windowcontrols.js';
import { DeesAppuiProfileDropdown } from '../dees-appui-profiledropdown/dees-appui-profiledropdown.js';
import { DeesContextmenu } from '../../00group-overlay/dees-contextmenu/dees-contextmenu.js';
import '../dees-appui-location/dees-appui-location.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-appui-appbar': DeesAppuiBar;
  }
}

@customElement('dees-appui-appbar')
export class DeesAppuiBar extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['App UI'];

  // INSTANCE PROPERTIES
  @property({ type: Array })
  accessor menuItems: interfaces.IAppBarMenuItem[] = [];

  @property({ type: String })
  accessor breadcrumbs: string = '';

  @property({ type: String })
  accessor breadcrumbSeparator: string = ' > ';

  @property({ attribute: false })
  accessor location: interfaces.IAppLocation | undefined;

  @property({ type: Boolean })
  accessor showWindowControls: boolean = true;


  @property({ type: Object })
  accessor user: {
    name: string;
    email?: string;
    avatar?: string;
    status?: 'online' | 'offline' | 'busy' | 'away';
  } | undefined = undefined;

  @property({ type: Array })
  accessor profileMenuItems: (plugins.tsclass.website.IMenuItem & { shortcut?: string } | { divider: true })[] = [];

  @property({ type: Boolean })
  accessor showSearch: boolean = false;

  // Activity log toggle
  @property({ type: Boolean })
  accessor showActivityLogToggle: boolean = false;

  @property({ type: Number })
  accessor activityLogCount: number = 0;

  @property({ type: Boolean })
  accessor activityLogActive: boolean = false;

  // STATE
  @state()
  accessor activeMenu: string | null = null;

  @state()
  accessor isProfileDropdownOpen: boolean = false;

  /** The currently-open portaled profile dropdown, if any. */
  private profileDropdownRef?: DeesAppuiProfileDropdown;

  /** The currently-open top-level menu's submenu overlay, if any. */
  private openMenuContextRef?: DeesContextmenu;
  private menuGeneration = 0;
  private pendingMenuOpen?: Promise<void>;
  private profileGeneration = 0;

  public static styles = appuiAppbarStyles;

  constructor() {
    super();
    this.registerStartupFunction(async signal => {
      await this.updateComplete;
      if (signal.aborted) return;
      const observer = new ResizeObserver(() => {
        if (!this.activeMenu) return;
        const trigger = this.shadowRoot?.querySelector<HTMLElement>(`[data-item-id="${this.activeMenu}"]`);
        if (trigger && !trigger.getClientRects().length) {
          this.closeOpenMenu();
          this.shadowRoot?.querySelector<HTMLElement>('.compact-menus .menuItem')?.focus();
        }
      });
      observer.observe(this);
      window.addEventListener('blur', () => this.closeOpenMenu(), { signal });
      signal.addEventListener('abort', () => observer.disconnect(), { once: true });
    });
  }

  // INSTANCE
  public render(): TemplateResult {
    return renderAppuiAppbar(this);
  }

  public renderMenuItems(): TemplateResult {
    return html`
      <div class="full-menus">${this.menuItems.map((item, index) => this.renderMenuItem(item, `menu-${index}`))}</div>
      ${this.menuItems.length > 1 ? html`<div class="compact-menus">${this.renderMenuItem({ name: 'Menu', iconName: 'Menu', action: async () => {}, submenu: this.menuItems }, 'menu-overflow')}</div>` : ''}
    `;
  }

  private renderMenuItem(item: interfaces.IAppBarMenuItem, itemId: string): TemplateResult {
    if ('divider' in item && item.divider) {
      return html`<div class="dropdown-divider"></div>`;
    }

    const menuItem = item as interfaces.IAppBarMenuItemRegular;
    const isActive = this.activeMenu === itemId;
    const hasSubmenu = menuItem.submenu && menuItem.submenu.length > 0;
    const isCheckable = menuItem.checked !== undefined;

    return html`
      <button type="button"
        class="menuItem ${isActive ? 'active' : ''} ${menuItem.checked ? 'checked' : ''}"
        ?disabled=${menuItem.disabled}
        tabindex="${menuItem.disabled ? -1 : 0}"
        data-item-id="${itemId}"
        @click=${(e: MouseEvent) => this.handleMenuClick(menuItem, itemId, e)}
        @pointerenter=${(e: PointerEvent) => this.handleMenuItemHover(menuItem, itemId, e)}
        @keydown=${(e: KeyboardEvent) => this.handleMenuKeydown(e, menuItem, itemId)}
        role=${isCheckable ? (menuItem.radioGroup ? 'menuitemradio' : 'menuitemcheckbox') : 'menuitem'}
        .ariaChecked=${isCheckable ? String(menuItem.checked) : null}
        aria-haspopup="${hasSubmenu}"
        aria-expanded="${isActive}"
      >
        ${menuItem.iconName ? html`<dees-icon .icon="${normalizeIconName(menuItem.iconName)}"></dees-icon>` : ''}
        ${menuItem.name}
      </button>
    `;
  }

  public renderBreadcrumbs(): TemplateResult {
    if (!this.breadcrumbs) {
      return html``;
    }

    const parts = this.breadcrumbs.split(this.breadcrumbSeparator);
    return html`
      ${parts.map((part, index) => html`
        ${index > 0 ? html`<span class="breadcrumb-separator">${this.breadcrumbSeparator}</span>` : ''}
        <button type="button"
          class="breadcrumb-item" 
          @click=${() => this.handleBreadcrumbClick(part, index)}
        >
          ${part}
        </button>
      `)}
    `;
  }

  public renderAccountSection(): TemplateResult {
    return html`
      ${this.showSearch ? html`
        <button type="button" class="search-icon" aria-label="Search workspace" title="Search workspace"
          @click=${this.handleSearchClick}
        ><dees-icon icon="lucide:Search" iconSize="16"></dees-icon></button>
      ` : ''}
      ${this.user ? html`
        <button type="button" class="user-info" aria-label=${`Account: ${this.user.name}`} aria-haspopup="menu" aria-expanded=${this.isProfileDropdownOpen} @click=${(e: MouseEvent) => this.handleUserClick(e)}>
          <div class="user-avatar">
            ${this.user.avatar ?
              html`<img src="${this.user.avatar}" alt="${this.user.name}">` :
              html`${this.user.name.charAt(0).toUpperCase()}`
            }
            ${this.user.status ? html`
              <div class="user-status ${this.user.status}"></div>
            ` : ''}
          </div>
          <span>${this.user.name}</span>
        </button>
      ` : ''}
      ${this.showActivityLogToggle ? html`
        <button type="button" aria-label="Activity log" aria-expanded=${this.activityLogActive}
          class="activity-toggle ${this.activityLogActive ? 'active' : ''}"
          @click=${this.handleActivityToggle}
          title="Activity Log"
        >
          <dees-icon .icon=${'lucide:activity'}></dees-icon>
          ${this.activityLogCount > 0 ? html`
            <span class="activity-badge">${this.activityLogCount > 99 ? '99+' : this.activityLogCount}</span>
          ` : ''}
        </button>
      ` : ''}
    `;
  }

  // Event handlers
  private handleMenuClick(item: interfaces.IAppBarMenuItemRegular, itemId: string, event?: MouseEvent | KeyboardEvent) {
    if (item.disabled) return;

    if (item.submenu && item.submenu.length > 0) {
      // Toggle: clicking the already-open top-level menu closes it.
      if (this.activeMenu === itemId) {
        this.closeOpenMenu();
        return;
      }
      this.showMenu(item, itemId, event?.currentTarget as HTMLElement | undefined);
    } else {
      // Execute action
      this.closeOpenMenu();
      if (item.action) {
        item.action();
      }
      this.dispatchEvent(new CustomEvent('menu-select', {
        detail: { item },
        bubbles: true,
        composed: true
      }));
    }
  }

  /** Open a menu without a pointer-blocking backdrop over its sibling headings. */
  private async showMenu(
    item: interfaces.IAppBarMenuItemRegular,
    itemId: string,
    triggerEl?: HTMLElement,
  ) {
    const generation = ++this.menuGeneration;
    this.activeMenu = itemId;
    const previousOpening = this.pendingMenuOpen;
    const opening = (async () => {
      await previousOpening;
      if (generation !== this.menuGeneration || !this.isConnected) return;

      // Replace the entire chain so no submenu, delayed hover, or focused item
      // from the previous heading survives the switch.
      const previousMenu = this.openMenuContextRef;
      this.openMenuContextRef = undefined;
      triggerEl?.focus({ preventScroll: true });
      await previousMenu?.destroy();
      if (generation !== this.menuGeneration || !this.isConnected) return;
      const menu = await DeesContextmenu.openContextMenuWithOptions(
        this.synthTriggerEvent(triggerEl),
        this.toContextMenuItems(item.submenu!),
        { ownerElement: triggerEl, interactionRoot: this.shadowRoot!.querySelector<HTMLElement>('.menus')! },
      );
      if (generation !== this.menuGeneration || !this.isConnected) {
        await menu?.destroy();
        return;
      }
      if (!menu?.isConnected) {
        this.closeOpenMenu();
        return;
      }
      this.openMenuContextRef = menu;
      menu.addEventListener('keydown', (event: KeyboardEvent) => {
        if (event.defaultPrevented || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        event.stopPropagation();
        this.focusNextMenuItem(itemId, event.key === 'ArrowRight' ? 1 : -1);
      });
      menu.addEventListener('closed', () => {
        if (this.openMenuContextRef !== menu) return;
        this.openMenuContextRef = undefined;
        this.activeMenu = null;
      }, { once: true });
    })();
    this.pendingMenuOpen = opening;
    try {
      await opening;
    } finally {
      if (this.pendingMenuOpen === opening) this.pendingMenuOpen = undefined;
    }
  }

  /** A minimal MouseEvent-shaped object positioning a fresh overlay under a trigger. */
  private synthTriggerEvent(triggerEl?: HTMLElement): MouseEvent {
    const rect = triggerEl?.getBoundingClientRect();
    const clientX = rect ? rect.left : 0;
    const clientY = rect ? rect.bottom + 4 : 0;
    return new MouseEvent('contextmenu', { clientX, clientY });
  }

  /**
   * Menu-bar behavior: once a top-level menu is open, hovering a *different*
   * top-level item switches the open menu to it (or closes it for an item
   * without a submenu).
   */
  private handleMenuItemHover(
    item: interfaces.IAppBarMenuItemRegular,
    itemId: string,
    event: PointerEvent,
  ) {
    if (event.pointerType === 'touch') return;
    if (this.activeMenu === null || this.activeMenu === itemId || item.disabled) {
      return;
    }
    if (item.submenu && item.submenu.length > 0) {
      this.showMenu(item, itemId, event.currentTarget as HTMLElement);
    } else {
      this.closeOpenMenu();
    }
  }

  /** Close the open top-level menu, including any pending asynchronous open. */
  private closeOpenMenu() {
    this.menuGeneration++;
    this.openMenuContextRef?.destroy();
    this.openMenuContextRef = undefined;
    this.activeMenu = null;
  }

  /**
   * Map appbar menu items to the dees-contextmenu item shape: qualify bare
   * Lucide icon names, carry shortcut/disabled/checked/submenu, and wire each
   * action to also emit the appbar's `menu-select` event.
   */
  private toContextMenuItems(
    items: interfaces.IAppBarMenuItem[],
  ): Parameters<typeof DeesContextmenu.openContextMenuWithOptions>[1] {
    return items.map((it) => {
      if ('divider' in it && it.divider) {
        return { divider: true };
      }
      const mi = it as interfaces.IAppBarMenuItemRegular;
      const iconName = mi.iconName ? normalizeIconName(mi.iconName) : undefined;
      const hasSubmenu = !!(mi.submenu && mi.submenu.length);
      return {
        name: mi.name,
        iconName,
        shortcut: mi.shortcut,
        disabled: mi.disabled,
        checked: mi.checked,
        radioGroup: mi.radioGroup,
        submenu: hasSubmenu ? this.toContextMenuItems(mi.submenu!) : undefined,
        action: async () => {
          if (mi.action) await mi.action();
          this.dispatchEvent(
            new CustomEvent('menu-select', { detail: { item: mi }, bubbles: true, composed: true }),
          );
        },
      };
    }) as Parameters<typeof DeesContextmenu.openContextMenuWithOptions>[1];
  }

  private handleMenuKeydown(e: KeyboardEvent, item: interfaces.IAppBarMenuItemRegular, itemId: string) {
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        this.handleMenuClick(item, itemId, e);
        break;
      case 'ArrowDown':
        if (item.submenu) {
          e.preventDefault();
          void this.showMenu(item, itemId, e.currentTarget as HTMLElement);
        }
        break;
      case 'Escape':
        this.closeOpenMenu();
        break;
      case 'Tab':
        // Let default tab navigation work but close dropdown
        if (this.activeMenu === itemId) {
          this.closeOpenMenu();
        }
        break;
      case 'ArrowRight':
        e.preventDefault();
        this.focusNextMenuItem(itemId, 1);
        break;
      case 'ArrowLeft':
        e.preventDefault();
        this.focusNextMenuItem(itemId, -1);
        break;
    }
  }

  private handleBreadcrumbClick(breadcrumb: string, index: number) {
    this.dispatchEvent(new CustomEvent('breadcrumb-navigate', { 
      detail: { breadcrumb, index },
      bubbles: true,
      composed: true 
    }));
  }

  private handleSearchClick() {
    this.dispatchEvent(new CustomEvent('search-click', {
      bubbles: true,
      composed: true
    }));
  }

  private handleActivityToggle() {
    this.dispatchEvent(new CustomEvent('activity-toggle', {
      bubbles: true,
      composed: true
    }));
  }

  private async handleUserClick(event: MouseEvent) {
    // Toggle: a second click on the trigger closes the open dropdown.
    if (this.isProfileDropdownOpen) {
      await this.closeProfileMenu();
      return;
    }

    const trigger = event.currentTarget as HTMLElement;
    const generation = ++this.profileGeneration;
    this.isProfileDropdownOpen = true;

    // Emit the event for backward compatibility.
    this.dispatchEvent(new CustomEvent('user-menu-open', {
      bubbles: true,
      composed: true,
    }));

    // Portal the dropdown to escape the appbar's stacking context and account clip.
    const dropdown = await DeesAppuiProfileDropdown.openFromTrigger(trigger, {
      user: this.user,
      menuItems: this.profileMenuItems,
      onSelect: (detail) => {
        this.dispatchEvent(new CustomEvent('profile-menu-select', {
          detail,
          bubbles: true,
          composed: true,
        }));
      },
    });
    if (generation !== this.profileGeneration || !this.isConnected) {
      await dropdown.closePortal();
      return;
    }
    if (!dropdown.isConnected) {
      this.isProfileDropdownOpen = false;
      return;
    }
    this.profileDropdownRef = dropdown;
    dropdown.addEventListener('portal-closed', () => {
      if (this.profileDropdownRef !== dropdown) return;
      this.profileDropdownRef = undefined;
      this.isProfileDropdownOpen = false;
    }, { once: true });
  }

  private async closeProfileMenu() {
    this.profileGeneration++;
    const dropdown = this.profileDropdownRef;
    this.profileDropdownRef = undefined;
    this.isProfileDropdownOpen = false;
    await dropdown?.closePortal();
  }

  // Lifecycle
  async disconnectedCallback() {
    this.closeOpenMenu();
    void this.closeProfileMenu();
    await super.disconnectedCallback();
  }

  private focusNextMenuItem(currentItemId: string, direction: number) {
    const menuItems = Array.from(this.renderRoot.querySelectorAll<HTMLElement>('.menuItem')).filter(item => item.getClientRects().length > 0);
    const currentIndex = menuItems.findIndex(item => item.getAttribute('data-item-id') === currentItemId);
    
    if (currentIndex === -1 || menuItems.length < 2) return;
    
    let nextIndex = currentIndex + direction;
    
    // Wrap around
    if (nextIndex < 0) {
      nextIndex = menuItems.length - 1;
    } else if (nextIndex >= menuItems.length) {
      nextIndex = 0;
    }
    
    // Find next non-disabled item
    let attempts = 0;
    while (attempts < menuItems.length) {
      const nextItem = menuItems[nextIndex] as HTMLElement;
      if (!nextItem.hasAttribute('disabled')) {
        nextItem.focus();
        if (this.activeMenu) {
          const nextId = nextItem.dataset.itemId!;
          const index = Number(nextId.slice('menu-'.length));
          const item = this.menuItems[index];
          if (item && 'submenu' in item && item.submenu?.length) {
            void this.showMenu(item, nextId, nextItem);
          } else {
            this.closeOpenMenu();
          }
        }
        break;
      }
      nextIndex = (nextIndex + direction + menuItems.length) % menuItems.length;
      attempts++;
    }
  }
}
